import { DownloadOutlined } from "@ant-design/icons"
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Empty,
  Flex,
  Radio,
  Select,
  Skeleton,
  Spin,
  Typography,
  notification
} from "antd"
import dayjs, { type Dayjs } from "dayjs"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts"

import { RoutesPaths } from "../../../../constants/routes.ts"
import {
  formatDays,
  formatMoney,
  formatPercent,
  formatPoints,
  formatScore
} from "../format"
import { formatMetric, metricValue } from "../member/OverviewView"
import { downloadPerformanceCsv } from "../member/api"
import type { Block } from "../member/types"
import type { TrendMetric } from "../member/use-member-url-state"
import { dateToPeriodKey, periodKeyToDate } from "../period-keys"
import type { PerformanceGrain } from "../types"
import { COMPARISON_PATH, comparisonParams, fetchComparison } from "./api"
import type { ComparisonResponse } from "./types"
import {
  type ComparisonMetric,
  useComparisonUrlState
} from "./use-comparison-url-state"

const { Text } = Typography

// Ten clearly different hues; grey is kept for the company line.
const COLORS = [
  "#1f77b4",
  "#d62728",
  "#2ca02c",
  "#ff7f0e",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#17becf",
  "#bcbd22",
  "#393b79"
]
const RANGES: { grain: PerformanceGrain; key: string }[] = [
  { grain: "month", key: "months" },
  { grain: "quarter", key: "quarters" },
  { grain: "year", key: "years" }
]
export const COMPARISON_METRICS: ComparisonMetric[] = [
  "avg_delay_days",
  "on_time_percent",
  "gap_amount",
  "overrun_amount",
  "margin_variance_points",
  "schedule_score",
  "financial_score",
  "projects"
]

// Axis ticks read like the cells: $38K, 75%, 4 d — never a raw 38000.
const axisTick = (metric: ComparisonMetric, value: number): string => {
  if (metric === "gap_amount" || metric === "overrun_amount")
    return formatMoney(value)
  if (metric === "on_time_percent") return `${value}%`
  if (metric === "avg_delay_days") return `${value} d`
  return String(value)
}

const MONEY_METRICS: ComparisonMetric[] = [
  "gap_amount",
  "overrun_amount",
  "margin_variance_points"
]

const valueOf = (block: Block, metric: ComparisonMetric): number | null =>
  metric === "projects"
    ? block.projects
    : metricValue(block, metric as TrendMetric)

const format = (metric: ComparisonMetric, value: number | null): string =>
  metric === "projects"
    ? String(value ?? 0)
    : formatMetric(metric as TrendMetric, value)

type Load =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; data: ComparisonResponse }

// Several members side by side over 12 months, 4 quarters or 3 years. Every
// figure is the server's, summed per member and period; a period with no closed
// project is a gap, never a zero.
export function ComparisonTab() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { query, metric: urlMetric, update } = useComparisonUrlState()
  const [searchParams] = useSearchParams()
  const [state, setState] = useState<Load>({ status: "loading" })
  const [refreshing, setRefreshing] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const key = JSON.stringify(query)

  useEffect(() => {
    let current = true
    setState((prev) => (prev.status === "ready" ? prev : { status: "loading" }))
    setRefreshing(true)
    fetchComparison(query)
      .then((data) => current && setState({ status: "ready", data }))
      .catch(() => current && setState({ status: "error" }))
      .finally(() => current && setRefreshing(false))
    return () => {
      current = false
    }
  }, [key, attempt])

  const data = state.status === "ready" ? state.data : undefined
  const shown = data?.members.map((m) => m.id) ?? []
  // An id the server dropped (no longer a PM, an old link) is not shown as a bare id.
  const available = new Set(data?.available_members.map((m) => m.id))
  const selected = query.memberIds.length
    ? query.memberIds.filter((id) => !data || available.has(id))
    : shown
  const metrics = data?.amounts_visible
    ? COMPARISON_METRICS
    : COMPARISON_METRICS.filter((m) => !MONEY_METRICS.includes(m))
  const metric = metrics.includes(urlMetric) ? urlMetric : "avg_delay_days"
  const periodLabel = (p: { label: string; partial?: boolean }) =>
    p.partial ? `${p.label} ${t("teamPerformance.comparison.toDate")}` : p.label

  const chartData = useMemo(
    () =>
      data?.periods.map((period, index) => {
        const row: Record<string, string | number | null> = {
          label: periodLabel(period)
        }
        for (const member of data.members) {
          row[member.id] = valueOf(member.points[index], metric)
          row[`${member.id}__n`] = member.points[index].projects
        }
        row.company = valueOf(data.company.points[index], metric)
        row.company__n = data.company.points[index].projects
        return row
      }) ?? [],
    [data, metric]
  )

  // The member page's Back returns here, to the same comparison.
  const back = () => {
    const keep = new URLSearchParams({ tab: "comparison" })
    searchParams.forEach((value, name) => {
      if (name.startsWith("cmp_")) keep.append(name, value)
    })
    return keep.toString()
  }

  const openPeriod = (memberId: string, period: string) => {
    const params = new URLSearchParams({
      grain: query.grain,
      from: period,
      to: period,
      view: "projects",
      period,
      back: back()
    })
    navigate(
      `${RoutesPaths.team_performance_member(memberId)}?${params.toString()}`
    )
  }

  const openMember = (memberId: string) => {
    if (!data) return
    const params = new URLSearchParams({
      grain: query.grain,
      from: data.filters.from,
      to: data.filters.to,
      back: back()
    })
    navigate(
      `${RoutesPaths.team_performance_member(memberId)}?${params.toString()}`
    )
  }

  const exportCsv = () => {
    if (!data) return
    downloadPerformanceCsv(
      COMPARISON_PATH,
      comparisonParams(query),
      `team-performance-comparison-${data.filters.from}-${data.filters.to}.csv`
    ).catch(() =>
      notification.error({ message: t("teamPerformance.exportFailed") })
    )
  }

  const metricLabel = (m: ComparisonMetric) =>
    m === "projects"
      ? t("teamPerformance.comparison.closedProjects")
      : t(`teamPerformance.member.metrics.${m}`)

  return (
    <Flex vertical gap="middle">
      <Flex justify="space-between" align="flex-end" wrap gap="middle">
        <Flex gap="middle" wrap align="flex-end">
          <Flex vertical gap={2}>
            <Text type="secondary" className="text-xs">
              {t("teamPerformance.comparison.range")}
            </Text>
            <div
              role="radiogroup"
              aria-label={t("teamPerformance.comparison.range")}
            >
              <Radio.Group
                optionType="button"
                value={query.grain}
                onChange={(e) =>
                  update({
                    grain: e.target.value as PerformanceGrain,
                    // The busiest members differ per range: keep the ones on screen.
                    ...(query.memberIds.length ? {} : { memberIds: shown })
                  })
                }
                options={RANGES.map((r) => ({
                  value: r.grain,
                  label: t(`teamPerformance.comparison.ranges.${r.key}`)
                }))}
              />
            </div>
          </Flex>
          <Flex vertical gap={2}>
            <Text type="secondary" className="text-xs">
              {t("teamPerformance.comparison.ending")}
            </Text>
            <DatePicker
              aria-label={t("teamPerformance.comparison.ending")}
              picker={query.grain}
              allowClear={false}
              value={periodKeyToDate(
                query.grain,
                query.to ??
                  (data?.filters.grain === query.grain
                    ? data.filters.to
                    : undefined)
              )}
              disabledDate={(d: Dayjs) =>
                dateToPeriodKey(query.grain, d) >
                dateToPeriodKey(query.grain, dayjs())
              }
              format={
                query.grain === "quarter"
                  ? "YYYY-[Q]Q"
                  : query.grain === "year"
                    ? "YYYY"
                    : "MMM YYYY"
              }
              onChange={(d: Dayjs | null) =>
                d && update({ to: dateToPeriodKey(query.grain, d) })
              }
            />
          </Flex>
          <Flex vertical gap={2} className="min-w-72 flex-1">
            <Text type="secondary" className="text-xs">
              {t("teamPerformance.comparison.members")}
            </Text>
            <Select<string[]>
              mode="multiple"
              aria-label={t("teamPerformance.comparison.members")}
              maxCount={10}
              value={selected}
              placeholder={t("teamPerformance.comparison.membersPlaceholder")}
              onChange={(ids) => update({ memberIds: ids })}
              optionFilterProp="label"
              options={(data?.available_members ?? []).map((m) => ({
                value: m.id,
                label: m.name
              }))}
            />
          </Flex>
          <Flex vertical gap={2}>
            <Text type="secondary" className="text-xs">
              {t("teamPerformance.member.metric")}
            </Text>
            <Select<ComparisonMetric>
              aria-label={t("teamPerformance.member.metric")}
              className="min-w-48"
              value={metric}
              onChange={(m) => update({ metric: m })}
              options={metrics.map((m) => ({
                value: m,
                label: metricLabel(m)
              }))}
            />
          </Flex>
        </Flex>
        <Flex gap="small" align="center">
          {refreshing && data && <Spin size="small" />}
          <Button
            icon={<DownloadOutlined />}
            onClick={exportCsv}
            disabled={!data}
          >
            {t("teamPerformance.export")}
          </Button>
        </Flex>
      </Flex>

      {state.status === "loading" && (
        <Skeleton active paragraph={{ rows: 10 }} />
      )}
      {state.status === "error" && (
        <Alert
          type="error"
          showIcon
          message={t("teamPerformance.states.loadFailed")}
          action={
            <Button onClick={() => setAttempt((n) => n + 1)}>
              {t("teamPerformance.states.retry")}
            </Button>
          }
        />
      )}
      {data && !data.members.length && (
        <Empty description={t("teamPerformance.comparison.noMembers")} />
      )}
      {data && data.members.length > 0 && (
        <Flex vertical gap={24}>
          <Card
            size="small"
            title={t("teamPerformance.comparison.chartTitle", {
              metric: metricLabel(metric),
              from: data.filters.from_label,
              to: data.filters.to_label
            })}
          >
            <div aria-hidden="true">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={chartData}
                  margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" />
                  <YAxis
                    width={64}
                    tickFormatter={(value: number) => axisTick(metric, value)}
                  />
                  <Tooltip
                    formatter={(value, _name, item) =>
                      `${format(metric, value as number | null)} (${t(
                        "teamPerformance.comparison.projectCount",
                        {
                          count: Number(
                            item.payload?.[`${String(item.dataKey)}__n`] ?? 0
                          )
                        }
                      )})`
                    }
                  />
                  <Legend />
                  {data.members.map((member, index) => (
                    <Line
                      key={member.id}
                      type="monotone"
                      dataKey={member.id}
                      name={member.name}
                      stroke={COLORS[index % COLORS.length]}
                      strokeWidth={2}
                      connectNulls={false}
                      dot={{ r: 3 }}
                    />
                  ))}
                  <Line
                    type="monotone"
                    dataKey="company"
                    name={t("teamPerformance.types.company")}
                    stroke="#9aa5b1"
                    strokeDasharray="5 4"
                    connectNulls={false}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card
            size="small"
            title={t("teamPerformance.comparison.tableTitle", {
              metric: metricLabel(metric)
            })}
          >
            <PeriodTable
              data={data}
              metric={metric}
              format={format}
              periodLabel={periodLabel}
              onOpen={openPeriod}
              onMember={openMember}
            />
          </Card>
          <TotalsTable data={data} onMember={openMember} />
        </Flex>
      )}
    </Flex>
  )
}

interface PeriodTableProps {
  data: ComparisonResponse
  metric: ComparisonMetric
  format: (metric: ComparisonMetric, value: number | null) => string
  periodLabel: (p: { label: string; partial?: boolean }) => string
  onOpen: (memberId: string, period: string) => void
  onMember: (memberId: string) => void
}

function PeriodTable({
  data,
  metric,
  format: show,
  periodLabel,
  onOpen,
  onMember
}: PeriodTableProps) {
  const { t } = useTranslation()
  // A count reads 0 when nothing closed; every other metric is a gap.
  const empty = metric === "projects" ? "0" : "—"
  const cell = (block: Block) =>
    block.projects ? show(metric, valueOf(block, metric)) : empty
  return (
    <div className="overflow-x-auto">
      <table
        aria-label={t("teamPerformance.comparison.periodTable")}
        className="w-full text-sm"
      >
        <thead>
          <tr className="text-left text-gray-500">
            <th className="py-1 pr-3 font-medium">
              {t("teamPerformance.table.member")}
            </th>
            {data.periods.map((p) => (
              <th key={p.key} className="py-1 pr-3 text-right font-medium">
                {periodLabel(p)}
              </th>
            ))}
            <th className="py-1 pr-3 text-right font-medium">
              {t("teamPerformance.comparison.total")}
            </th>
          </tr>
        </thead>
        <tbody>
          {data.members.map((member) => (
            <tr key={member.id} className="border-t border-gray-100">
              <td className="py-1 pr-3">
                <Button
                  type="link"
                  className="h-auto p-0"
                  onClick={() => onMember(member.id)}
                >
                  {member.name}
                </Button>
              </td>
              {member.points.map((point, index) =>
                point.projects ? (
                  <td key={point.period} className="py-1 pr-3 text-right">
                    <Button
                      type="link"
                      size="small"
                      className="h-auto p-0"
                      aria-label={`${member.name}, ${data.periods[index].label}`}
                      onClick={() => onOpen(member.id, point.period)}
                    >
                      {cell(point)}
                    </Button>
                    <span className="text-xs text-gray-400">
                      {" "}
                      ({point.projects})
                    </span>
                  </td>
                ) : (
                  <td
                    key={point.period}
                    className="py-1 pr-3 text-right text-gray-400"
                  >
                    {empty}
                  </td>
                )
              )}
              <td className="py-1 pr-3 text-right font-medium">
                {cell(member.total)}
              </td>
            </tr>
          ))}
          <tr className="border-t border-gray-200 text-gray-500">
            <td className="py-1 pr-3">{t("teamPerformance.types.company")}</td>
            {data.company.points.map((point) => (
              <td key={point.period} className="py-1 pr-3 text-right">
                {cell(point)}
                {point.projects > 0 && (
                  <span className="text-xs text-gray-400">
                    {" "}
                    ({point.projects})
                  </span>
                )}
              </td>
            ))}
            <td className="py-1 pr-3 text-right">{cell(data.company.total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

function TotalsTable({
  data,
  onMember
}: {
  data: ComparisonResponse
  onMember: (id: string) => void
}) {
  const { t } = useTranslation()
  const money = (value: number | null) =>
    data.amounts_visible ? formatMoney(value) : "—"
  const rows = [
    ...data.members.map((m) => ({
      id: m.id,
      name: m.name,
      block: m.total,
      member: true
    })),
    {
      id: "company",
      name: t("teamPerformance.types.company"),
      block: data.company.total,
      member: false
    }
  ]
  return (
    <Card
      size="small"
      title={t("teamPerformance.comparison.totalsTitle", {
        from: data.filters.from_label,
        to: data.filters.to_label
      })}
    >
      <div className="overflow-x-auto">
        <table
          aria-label={t("teamPerformance.comparison.totalsTable")}
          className="w-full text-sm"
        >
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1 pr-3 font-medium">
                {t("teamPerformance.table.member")}
              </th>
              <th className="py-1 pr-3 text-right font-medium">
                {t("teamPerformance.table.projects")}
              </th>
              <th className="py-1 pr-3 text-right font-medium">
                {t("teamPerformance.table.onTime")}
              </th>
              <th className="py-1 pr-3 text-right font-medium">
                {t("teamPerformance.table.avgDelay")}
              </th>
              <th className="py-1 pr-3 text-right font-medium">
                {t("teamPerformance.comparison.lateOver14")}
              </th>
              <th className="py-1 pr-3 text-right font-medium">
                {t("teamPerformance.table.schedule")}
              </th>
              <th className="py-1 pr-3 text-right font-medium">
                {t("teamPerformance.kpi.costOverrun")}
              </th>
              <th className="py-1 pr-3 text-right font-medium">
                {t("teamPerformance.kpi.revenueGap")}
              </th>
              <th className="py-1 pr-3 text-right font-medium">
                {t("teamPerformance.member.metrics.margin_variance_points")}
              </th>
              <th className="py-1 pr-3 text-right font-medium">
                {t("teamPerformance.table.financial")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ id, name, block, member }) => (
              <tr
                key={id}
                className={`border-t border-gray-100 ${member ? "" : "text-gray-500"}`}
              >
                <td className="py-1 pr-3">
                  {member ? (
                    <Button
                      type="link"
                      className="h-auto p-0"
                      onClick={() => onMember(id)}
                    >
                      {name}
                    </Button>
                  ) : (
                    name
                  )}
                </td>
                <td className="py-1 pr-3 text-right">{block.projects}</td>
                <td className="py-1 pr-3 text-right">
                  {formatPercent(block.summary.on_time_percent)}
                </td>
                <td className="py-1 pr-3 text-right">
                  {formatDays(block.summary.avg_delay_days)}
                </td>
                <td className="py-1 pr-3 text-right">
                  {block.summary.late_over_14}
                </td>
                <td className="py-1 pr-3 text-right">
                  {formatScore(block.schedule_score)}
                </td>
                <td className="py-1 pr-3 text-right">
                  {money(block.summary.overrun_amount)}
                </td>
                <td className="py-1 pr-3 text-right">
                  {money(block.summary.gap_amount)}
                </td>
                <td className="py-1 pr-3 text-right">
                  {data.amounts_visible
                    ? formatPoints(block.summary.margin_variance_points)
                    : "—"}
                </td>
                <td className="py-1 pr-3 text-right">
                  {formatScore(block.financial_score)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
